import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { DesignStage } from "./models";
import type { DesignStageRow, DesignStageSnapshotIn } from "./models";
import type { AdminRootAccess } from "../rootAccess";
import type { TablesInsert, TablesUpdate } from "@/types/supabase-helpers";

export const DesignStagesStore = types
  .model("DesignStagesStore", {
    stages: types.array(DesignStage),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get stagesList() {
      return self.stages.slice();
    },
    getById(id: number) {
      return self.stages.find((s) => s.id === id);
    },
  }))
  .actions((self) => ({
    loadStages: flow(function* loadStages() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: DesignStageRow[] = yield repo.listDesignStages();
        self.stages.clear();
        data.forEach((s) => {
          self.stages.push(s as DesignStageSnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    /**
     * Create a design stage.
     *
     * A stage created here is NOT selectable by the LLM — the stage classification prompt
     * lists its stages literally instead of reading this table — so it only takes effect
     * where a stage is applied deterministically, e.g. tied to a document template.
     */
    createStage: flow(function* createStage(stage: TablesInsert<"design_stages">) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to create design stages");
        }
        const newStage: DesignStageRow = yield repo.createDesignStage(stage, userProfileId);
        self.stages.push(newStage as DesignStageSnapshotIn);
        return newStage;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateStage: flow(function* updateStage(id: number, updates: TablesUpdate<"design_stages">) {
      self.error = null;
      try {
        const updatedStage: DesignStageRow = yield repo.updateDesignStage(id, updates);
        const index = self.stages.findIndex((s) => s.id === id);
        if (index !== -1) {
          self.stages[index] = cast(updatedStage as DesignStageSnapshotIn);
        }
        return updatedStage;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.stages.clear();
      self.isLoading = false;
      self.error = null;
    },
  }));

export type DesignStagesStoreInstance = Instance<typeof DesignStagesStore>;
